import { Box, Chip, CircularProgress, IconButton, Link, TableCell, TableRow, Tooltip, Typography } from "@mui/material"
import Iconify from "minimals-template/components/Iconify"
import { confirm } from "lib/@dialogs/confirm"
import { If } from "lib/@components/switch"
import { Else } from "lib/@components/else"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { removeCopDocument } from "./controller/remove-cop-document.controller"
import { reimportCopDocument } from "./controller/reimport-cop-document.controller"

export function CopDocumentRow({ item }) {
    const isUrl = item.type === "url"
    const isSearch = item.type === "search"
    const icon = isSearch ? "mdi:web" : isUrl ? "mdi:link-variant" : "mdi:file-pdf-box"
    const iconColor = isSearch ? "warning.main" : isUrl ? "info.main" : "error.main"
    const { activeDocumentIds } = useBoundContext()
    const isProcessing = item.isProcessing || (activeDocumentIds?.has(item._id) ?? false)

    return (
        <TableRow>
            <TableCell>
                <Iconify icon={icon} sx={{ color: iconColor }} />
            </TableCell>
            <TableCell>
                {item.name}
                <If value={item.searchCategory}>
                    <Typography variant="caption" color="text.secondary" display="block">
                        {item.searchCategory}
                    </Typography>
                </If>
            </TableCell>
            <TableCell>
                <AuthorityLabel authority={item.sourceAuthority} />
            </TableCell>
            <TableCell sx={{ maxWidth: 250, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <If value={item.url}>
                    <Link
                        href={item.url}
                        target="_blank"
                        rel="noopener"
                        color="text.secondary"
                        underline="hover"
                        sx={{ fontSize: "inherit" }}
                    >
                        {item.url}
                    </Link>
                </If>
                <If value={!item.url && item.fileName}>
                    <Typography color="text.secondary" sx={{ fontSize: "inherit" }}>
                        {item.fileName}
                    </Typography>
                </If>
            </TableCell>
            <TableCell align="center">
                <StatusChip item={item} isProcessing={isProcessing} />
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                <DataStats item={item} />
            </TableCell>
            <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                <If value={isUrl || isSearch}>
                    <Tooltip title={isProcessing ? "Currently importing…" : "Re-import from URL"}>
                        <span>
                            <IconButton
                                aria-label="Re-import from URL"
                                size="small"
                                color="primary"
                                onClick={handleReimport}
                                disabled={isProcessing}
                            >
                                <Iconify icon="mdi:refresh" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </If>
                <Tooltip title="Delete">
                    <IconButton size="small" color="secondary" onClick={handleDelete}>
                        <Iconify icon="mdi:delete" />
                    </IconButton>
                </Tooltip>
            </TableCell>
        </TableRow>
    )

    async function handleReimport() {
        if (
            await confirm(
                `Re-import "${item.name}" from its URL?\n\nThis will re-scrape the page, follow links to PDFs, and translate non-English content.`,
                "Re-import Document"
            )
        ) {
            await reimportCopDocument({ id: item._id })
        }
    }

    async function handleDelete() {
        if (await confirm(`Delete document "${item.name}"?`, "Delete Document")) {
            await removeCopDocument({ id: item._id })
        }
    }
}

const AUTHORITY_CONFIG = {
    government: { label: "Government", color: "success.dark", icon: "mdi:bank" },
    "standards-body": { label: "Standards Body", color: "info.dark", icon: "mdi:certificate" },
    "trade-association": { label: "Trade Assoc.", color: "#c2410c", icon: "mdi:account-group" },
    manufacturer: { label: "Manufacturer", color: "text.primary", icon: "mdi:factory" },
}

function AuthorityLabel({ authority }) {
    const config = AUTHORITY_CONFIG[authority]
    if (config) {
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: config.color }}>
                <Iconify icon={config.icon} width={14} height={14} />
                <Typography variant="body2" color="inherit" noWrap>
                    {config.label}
                </Typography>
            </Box>
        )
    }
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.secondary" }}>
            <Iconify icon="mdi:account-edit" width={14} height={14} />
            <Typography variant="body2" fontWeight="bold" color="inherit" noWrap>
                User Supplied
            </Typography>
        </Box>
    )
}

const CHIP_SX = {
    ready: { bgcolor: "rgba(0,171,85,0.14)", color: "text.primary" },
    extracted: { bgcolor: "rgba(26,140,180,0.12)", color: "text.primary" },
    pending: { bgcolor: "rgba(0,0,0,0.06)", color: "text.primary" },
    error: { bgcolor: "rgba(255,72,66,0.12)", color: "error.main" },
}

function StatusChip({ item, isProcessing }) {
    if (isProcessing) {
        return (
            <Chip
                size="small"
                label="Processing"
                icon={
                    <CircularProgress
                        size={10}
                        thickness={5}
                        sx={{ color: "inherit !important", ml: "6px !important" }}
                    />
                }
                sx={{ bgcolor: "rgba(26,140,180,0.12)", color: "info.dark" }}
            />
        )
    }
    if (item.hasCuratedText) return <Chip label="Ready" size="small" sx={CHIP_SX.ready} />
    if (item.hasExtractedText) return <Chip label="Extracted" size="small" sx={CHIP_SX.extracted} />
    if (item.importError) {
        return (
            <Tooltip title={item.importError} arrow>
                <Chip label="Error" size="small" sx={CHIP_SX.error} />
            </Tooltip>
        )
    }
    return <Chip label="Pending" size="small" sx={CHIP_SX.pending} />
}

function DataStats({ item }) {
    if (!item.hasExtractedText && !item.hasCuratedText) return null

    const extractedKb = toKb(item.extractedTextLength)
    const curatedKb = item.hasCuratedText ? toKb(item.curatedTextLength) : null
    const pct =
        item.hasCuratedText && item.extractedTextLength > 0
            ? Math.round((item.curatedTextLength / item.extractedTextLength) * 100)
            : null

    const tooltipTitle = item.hasCuratedText
        ? `${item.curatedTextLength.toLocaleString()} of ${item.extractedTextLength.toLocaleString()} chars are maintenance-relevant`
        : ""

    return (
        <Tooltip title={tooltipTitle}>
            <Box sx={{ cursor: "default" }}>
                <Typography variant="caption" display="block" color="text.secondary" noWrap>
                    {extractedKb} KB captured
                </Typography>
                <If value={item.hasCuratedText}>
                    <Typography variant="caption" display="block" color="success.main" noWrap>
                        {curatedKb} KB maint. ({pct ?? "—"}%)
                    </Typography>
                    <Else>
                        <Typography variant="caption" display="block" color="text.disabled" noWrap>
                            — maint.
                        </Typography>
                    </Else>
                </If>
            </Box>
        </Tooltip>
    )
}

function toKb(chars) {
    return (chars / 1024).toFixed(1)
}
